import * as UsersActions from './users.actions';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
  catchError,
  distinctUntilChanged,
  finalize,
  map,
  mergeMap,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs/operators';

import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { IProcessV3 } from '@literax/components/platform/profiles/types/user-ptofiles';
import {
  EOperatorFilterField,
  ETypeFilterField,
} from '@literax/enums/query-parameters.enum';
import { IFilterField } from '@literax/interfaces/query-parameters.interface';
import { ErrorHandlerService } from '@literax/modules/shared/services/error-handler/error-handler.service';
import { LiteraxNotificationServiceService } from '@literax/services/literax-notification-service.service';
import { I18nToastrService } from '@literax/services/translate/i18n-toastr.service';
import { IAppState } from '@literax/store';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { IProfile } from '../../profiles/models/profiles';
import { IDepartmentReceivers } from '../models/users.interface';
import { UsersServices } from '../services/users.services';

@Injectable()
export class UsersEffect {
  constructor(
    private actions$: Actions,
    private store: Store<IAppState>,
    private usersServices: UsersServices,
    private toastr: LiteraxNotificationServiceService,
    private toastrReg: I18nToastrService,
    private router: Router,
    private errorHandler: ErrorHandlerService
  ) {}

  getAllV3$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        UsersActions.getAllUsersV3,
        UsersActions.updateUsersV3ByIdSuccess,
        UsersActions.deleteUsersV3Success,
        UsersActions.saveUsersV3Success,
        UsersActions.changeSortColumn,
        UsersActions.changeUserKindFilter
      ),
      withLatestFrom(this.store.select((state) => state.users.queryParameters)),
      withLatestFrom(this.store.select((state) => state.users.sortColumn)),
      withLatestFrom(this.store.select((state) => state.users.userKind)),
      distinctUntilChanged(),
      switchMap(([[[action, queryParameters], sort], userKind]) => {
       
        // Ensure filterFields are of the correct type
        const filterFields: IFilterField[] = (
          queryParameters.filterFields || []
        ).map((field: any) => ({
          ...field,
          type:
            typeof field.type === 'string'
              ? ETypeFilterField[field.type as keyof typeof ETypeFilterField]
              : field.type,
        }));
        if (userKind) {
          
          filterFields.push({
            name: 'user_kind',
            type: ETypeFilterField.TEXT,
            operator: EOperatorFilterField.EQ,
            term: userKind,
          });
        }
        return this.usersServices
          .getAllV3(
            queryParameters.pageNumber,
            queryParameters.pageSize,
            queryParameters.sortField,
            filterFields
          )
          .pipe(
            map((response) => {
              const res = response;
              return UsersActions.getAllUsersV3Success({
                payload: {
                  usersV3: res.body,
                  paginationV3: {
                    nextPage: res.headers.get('origon-paged-next-page'),
                    prevPage: res.headers.get('origon-paged-prev-page'),
                    currentPage: res.headers.get('origon-paged-current-page'),
                    sizePage: res.headers.get('origon-paged-size-page'),
                    totalRecords: res.headers.get('origon-paged-total-records'),
                    pages: res.headers.get('origon-paged-pages'),
                  },
                },
              });
            }),
            catchError((err: HttpErrorResponse) =>
              this._handleErrorAndShowModal(
                err?.error,
                UsersActions.getAllUsersV3Error({
                  payload: err?.error,
                })
              )
            )
          );
      })
    )
  );

  getDashboardV3$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        UsersActions.getUsersDashboardV3,
        UsersActions.saveUsersV3Success,
        UsersActions.deleteUsersV3Success
      ),
      switchMap(() =>
        this.usersServices.getDashboardV3().pipe(
          map((response) =>
            UsersActions.getUsersDashboardV3Success({
              payload: response.body as IProcessV3,
            })
          ),
          catchError((error) =>
            of(UsersActions.getUsersDashboardV3Error({ payload: error }))
          )
        )
      )
    )
  );

  updateV3$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UsersActions.updateUsersV3ById),
      mergeMap((action) =>
        this.usersServices
          .updateV3(action.clientUserId, action.patchOperations)
          .pipe(
            map(() => UsersActions.updateUsersV3ByIdSuccess()),
            finalize(() => {
              this.toastr.success('TOAST_NOTIFICATIONS.UPDATE_SUCCESS');
            }),
            catchError((err: HttpErrorResponse) =>
              this._handleErrorAndShowModal(
                err,
                UsersActions.getAllUsersV3Error({
                  payload: err?.error,
                })
              )
            )
          )
      )
    )
  );

  registerUserConfirmations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UsersActions.registerClientUsersConfirmationsV3),
      mergeMap((action) => {
        return this.usersServices.userConfirmV3(action.user, action.token).pipe(
          map((e) => {
            return UsersActions.registerClientUsersConfirmationsV3Sucess();
          }),
          tap(() => {
            this.toastr.success('TOAST_NOTIFICATIONS.SAVE_SUCCESS');
          }),
          catchError((err: HttpErrorResponse) =>
              this._handleErrorAndShowModal(
                err,
               of(UsersActions.errorConf({ error: err.error }), UsersActions.registerClientUsersConfirmationsV3Error({ error: err.error }))
              )
            )
        );
      })
    )
  );
  
  loginClientUserConfirmations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UsersActions.loginClientUsersConfirmations),
      mergeMap((action) =>
        this.usersServices.userLoginConfirm(action.token, action.b2cToken).pipe(
          map(() => UsersActions.loginClientUsersConfirmationsSucess()),
          catchError((response) =>
            of(UsersActions.errorloginConf({ error: response.error }))
          )
        )
      )
    )
  );

  loginClientUserSucess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UsersActions.loginClientUsersConfirmationsSucess),
        tap((action) => this.router.navigate(['platform', 'home']))
      ),
    { dispatch: false }
  );

  deleteV3$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UsersActions.deleteUsersV3),
      mergeMap((action) =>
        this.usersServices.deleteV3(action.clientUserId).pipe(
          map(() => UsersActions.deleteUsersV3Success()),
          finalize(() => {
            this.toastr.success('TOAST_NOTIFICATIONS.DELETE_SUCCESS');
          }),
          catchError((err: HttpErrorResponse) =>
            this._handleErrorAndShowModal(
              err,
              UsersActions.getAllUsersV3Error({
                payload: err?.error,
              })
            )
          )
        )
      )
    )
  );

  saveV3$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UsersActions.saveUsersV3),
      mergeMap((action) =>
        this.usersServices.saveV3(action.user).pipe(
          map(() => UsersActions.saveUsersV3Success()),
          finalize(() => {
            this.toastr.success('TOAST_NOTIFICATIONS.SAVE_SUCCESS');
          }),
          catchError((err: HttpErrorResponse) =>
            this._handleErrorAndShowModal(
              err,
              UsersActions.getAllUsersV3Error({
                payload: err?.error,
              })
            )
          )
        )
      )
    )
  );

  getUserProfiles$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UsersActions.getUserProfiles),
      mergeMap(() =>
        this.usersServices.getUserProfiles().pipe(
          map((response) =>
            UsersActions.getUserProfilesSuccess({
              payload: { profiles: response.profiles as IProfile[] },
            })
          ),
          catchError((err: HttpErrorResponse) => {
            this.evaluateErrorCode(err);
            return of(UsersActions.errorConf({ error: err.error }));
          })
        )
      )
    )
  );

  setDefaultUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UsersActions.SetDefaultUser),
      mergeMap((action) =>
        this.usersServices.setDefaultUser(action.payload).pipe(
          map(() => UsersActions.SetDefaultUserSuccess()),
          catchError((err: HttpErrorResponse) => {
            this.toastr.error('TOAST_NOTIFICATIONS.INTERNAL_SERVER_ERROR');
            return of(UsersActions.errorConf({ error: err.error }));
          })
        )
      )
    )
  );

  getReceivers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UsersActions.getReceivers),
      mergeMap((action) =>
        this.usersServices.getReceivers(action.excludeOneself).pipe(
          map((response) =>
            UsersActions.getReceiversSuccess({
              payload: {
                departmentReceivers: response as IDepartmentReceivers[],
              },
            })
          ),
          catchError((err: HttpErrorResponse) => {
            this.evaluateErrorCode(err);
            return of(UsersActions.errorConf({ error: err.error }));
          })
        )
      )
    )
  );

  disableTaxMessage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UsersActions.DisableTaxMessage),
      switchMap(() =>
        this.usersServices.disableTaxMessage().pipe(
          map(() => UsersActions.DisableTaxMessageSuccess()),
          catchError((error) =>
            of(UsersActions.DisableTaxMessageError({ payload: error }))
          )
        )
      )
    )
  );

  setErrorMessage(error: any) {
    const message = 'TOAST_NOTIFICATIONS.MESSAGE_GENERIC_SAVE';
    this.toastrReg.error(message, '');
  }

  evaluateErrorCode(error: HttpErrorResponse) {
    const Error = error.error.error;
    const StatusCodeHttp = Error.status;
    const Message = Error.title;
    switch (StatusCodeHttp) {
      case 401:
        console.error(Message, Error);
        break;
      case 500:
        this.toastr.error('TOAST_NOTIFICATIONS.INTERNAL_SERVER_ERROR');
        break;
      default:
        console.error(Message, Error);
        break;
    }
  }

  private _handleErrorAndShowModal(error, action) {
    return this.errorHandler.handle(error, action);
  }
}
